import { yupResolver } from "@hookform/resolvers/yup";
import { SelectChangeEvent } from "@mui/material";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetClientByIdMutation } from "../../../../../../../../api/services/client/clientApis";
import { POData } from "../../../../../../../../api/services/common/models";
import { useGetPaymentTerms } from "../../../../../../../../api/services/program/programApis";
import { ProjectKeyDetailsResponse } from "../../../../../../../../api/services/project/model";
import {
  useGetProjectInvoicingLanguages,
  useGetProjectKeyDetailsById,
  useGetProjectPrimaryDetailsById,
} from "../../../../../../../../api/services/project/projectApis";
import { ReactComponent as DownloadIcon } from "../../../../../../../../assets/images/download.svg";
import { ReactComponent as WarningIcon } from "../../../../../../../../assets/images/warning-icon.svg";
import { useGeneratePDF } from "../../../../../../../../common/hooks/useGeneratePDF";
import { InformationBadge } from "../../../../../../../../common/informationBadge/informationBadge";
import {
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiSelect,
  MuiTextField,
  MuiTypography,
} from "../../../../../../../../components";
import PermissionWrapper from "../../../../../../../../components/permissionWrapper/PermissionWrapper";
import {
  BE_ERROR_CODES,
  EMPTY_VALUE_DASH,
  MAX_LENGTH_30,
  MODULE_PERMISSIONS,
  SITE_CORE_COUNTRIES_VALUE,
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  getFormattedAmountWithCurrencyFromList,
  getParsedBEErrors,
  getValueOrBlank,
  getValueOrDash,
  hasKey,
  isEmpty,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { currencyListSelector } from "../../../../../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  keyDetailsSelector,
  projectBillingCountryIdSelector,
} from "../../../../../../../../redux/slices/project/projectSelector";
import {
  ProjectFeesAndExpenseRow,
  ProjectRaiseInvoicePODetailsFormKey,
} from "../../../../../../modelsAndSchemas/Models";
import { projectRaiseInvoiceSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { ProjectRaiseInvoiceModalSchema } from "./modelsAndSchema/ValidationSchemas";
import { ProjectProformaPdfTemplate } from "./ProjectProformaPdfTemplate";
import { ProjectRaiseInvoiceList } from "./ProjectRaiseInvoiceList";
import { ModalContainer } from "./ProjectRaiseInvoiceModal.styles";

export interface CaptureValuesParams {
  projectInvoiceDate: string;
  paymentTerms: string;
  poNumber: string;
  localInvoiceNumber: string;
}

type ProjectRaiseInvoiceModalProps = {
  open: boolean;
  modalTitle: string;
  totalBillingAmount: number;
  totalTaxAmount: number;
  totalInvoiceAmount: number;
  totalInvoiceCurrency: string;
  invoiceFeesAndExpense: ProjectFeesAndExpenseRow[];
  poNumber?: string;
  isProjectRaiseInvoiceError: boolean;
  projectRaiseInvoiceError: Error | null;
  onClose: () => void;
  onPrimaryButtonClick: () => void;
  callBackValues?: (params: CaptureValuesParams) => void;
};

const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };

export const ProjectRaiseInvoice: FC<ProjectRaiseInvoiceModalProps> = ({
  open,
  onClose,
  modalTitle,
  totalBillingAmount,
  totalTaxAmount,
  totalInvoiceAmount,
  totalInvoiceCurrency,
  invoiceFeesAndExpense,
  poNumber,
  onPrimaryButtonClick,
  callBackValues: captureValues,
  isProjectRaiseInvoiceError,
  projectRaiseInvoiceError: raiseInvoiceError,
}) => {
  const { fetchLabel, commonLabels, commonValidationMessages } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { generatePDF } = useGeneratePDF();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const {
    client: { id: clientId },
    projectName,
    id: projectId,
  } = useSelector(keyDetailsSelector) as ProjectKeyDetailsResponse;
  const billingCountryId = useSelector(projectBillingCountryIdSelector);

  const {
    data: projectKeyDetailsResponse,
    isFetching: isGetKeyProjectPending,
    isSuccess: isGetKeyProjectSuccess,
    refetchWithParams: refetchProjectKeyDetail,
  } = useGetProjectKeyDetailsById();

  const {
    mutateAsync: getClientById,
    data: clientData,
    isPending: isGetClientByIdPending,
    isSuccess: isGetClientByIdSuccess,
  } = useGetClientByIdMutation();

  const {
    mutateAsync: getGetPaymentTerms,
    data: paymentTermsList,
    isPending: isPaymentTermsListPending,
  } = useGetPaymentTerms();

  const {
    data: projectInvoicingLanguages,
    refetchWithParams: refetchProjectInvoicingLanguages,
  } = useGetProjectInvoicingLanguages();

  const {
    data: projectPrimaryDetails,
    isLoading: isProjectPrimaryDetailsPending,
    isSuccess: isProjectPrimaryDetailsSuccess,
    refetchWithParams: getProjectPrimaryDetails,
  } = useGetProjectPrimaryDetailsById();

  const {
    control,
    watch,
    getValues,
    setValue,
    handleSubmit,
    formState,
    setError,
  } = useForm({
    mode: "all",
    defaultValues: {
      [ProjectRaiseInvoicePODetailsFormKey.localInvoiceNumber]: "",
      [ProjectRaiseInvoicePODetailsFormKey.projectPONumber]: poNumber || "",
      [ProjectRaiseInvoicePODetailsFormKey.projectCurrentPOBalance]: "",
      [ProjectRaiseInvoicePODetailsFormKey.projectPOShortage]: "",
      [ProjectRaiseInvoicePODetailsFormKey.projectInvoiceDate]: dayjs().format(
        userCountryDateFormat,
      ),
      [ProjectRaiseInvoicePODetailsFormKey.projectPaymentTerms]: "",
    },
    resolver: yupResolver(
      ProjectRaiseInvoiceModalSchema(commonValidationMessages),
    ),
  });
  const pageId = "raise-invoice-modal";
  const [currentPOBalance, setCurrentPOBalance] = useState<number>(0);
  const [showPOBottomError, setShowPOBottomError] = useState<boolean>(false);
  const [poBottomErrorMessage, setPOBottomErrorMessage] = useState<string>("");

  const dispatch = useDispatch();

  const purchaseOrdersList = projectKeyDetailsResponse?.poNumber;

  const getCurrentSelectedPO = () => {
    const { poNumber } = getValues();
    if (purchaseOrdersList) {
      return purchaseOrdersList.find((po) => po.id === poNumber);
    }
  };

  useEffect(() => {
    if (projectId) {
      refetchProjectKeyDetail(projectId);
      refetchProjectInvoicingLanguages({
        projectId,
      });
    }
  }, [projectId]);

  useEffect(() => {
    getGetPaymentTerms();
  }, []);

  useEffect(() => {
    const { poNumber } = getValues();
    if (poNumber) {
      const poAmount = getCurrentSelectedPO()?.currentAmount ?? 0;
      setCurrentPOBalance(Number(poAmount));
    }
  }, [
    watch(ProjectRaiseInvoicePODetailsFormKey.projectPONumber),
    watch(ProjectRaiseInvoicePODetailsFormKey.projectPaymentTerms),
    watch(ProjectRaiseInvoicePODetailsFormKey.projectInvoiceDate),
  ]);

  useEffect(() => {
    if (hasShortagePO()) {
      setShowPOBottomError(true);
      setPOBottomErrorMessage(getRaiseInvoiceLabel("poShortageMessage"));
    } else if (hasEmptyAmountPO()) {
      setShowPOBottomError(true);
      setPOBottomErrorMessage(getRaiseInvoiceLabel("poNoAmountMessage"));
    } else {
      setShowPOBottomError(false);
      setPOBottomErrorMessage("");
    }
  }, [
    watch(ProjectRaiseInvoicePODetailsFormKey.projectPONumber),
    getCurrentSelectedPO()?.currentAmount,
    currentPOBalance,
    totalInvoiceAmount,
    projectKeyDetailsResponse?.poNumber,
  ]);
  const hasEmptyAmountPO = () => {
    const { poNumber } = getValues();
    return poNumber && isEmpty(getCurrentSelectedPO()?.currentAmount);
  };
  const hasShortagePO = () => {
    const { poNumber } = getValues();
    return (
      isNotEmpty(getCurrentSelectedPO()?.currentAmount) &&
      Boolean(poNumber && validatePOBalanceValue())
    );
  };

  useEffect(() => {
    if (isProjectRaiseInvoiceError) {
      const error = getParsedBEErrors(raiseInvoiceError);
      if (
        isNotEmpty(error) &&
        Array.isArray(error) &&
        hasKey(error[0], "errorCode")
      ) {
        const { errorCode, message: errorMessage } = error[0];

        const errorMapping = {
          [BE_ERROR_CODES.VALID_ACCOUNTING_PERIOD_NOT_DEFINED]: {
            formId: ProjectRaiseInvoicePODetailsFormKey.projectInvoiceDate,
            message: fetchLabel(
              projectRaiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
              projectRaiseInvoiceSiteCoreKeys.invoiceDateMustBeWithinTheAccountingPeriod,
            ),
          },
          [BE_ERROR_CODES.PO_IS_NOT_MAPPED_TO_PROJECT]: {
            formId: ProjectRaiseInvoicePODetailsFormKey.projectPONumber,
            message: errorMessage,
          },
        };

        const { formId, message } = errorMapping[errorCode] || {
          formId: ProjectRaiseInvoicePODetailsFormKey.projectInvoiceDate,
          message: errorMessage,
        };

        setError(formId, {
          type: "custom",
          message,
        });
      }
    }
  }, [isProjectRaiseInvoiceError]);

  useEffect(() => {
    const { poNumber, invoiceDate, paymentTerms, localInvoiceNumber } =
      getValues();
    if (poNumber && purchaseOrdersList) {
      const poAmount = getCurrentSelectedPO()?.currentAmount ?? 0;
      setCurrentPOBalance(Number(poAmount));
    } else {
      setCurrentPOBalance(0);
    }
    if (captureValues) {
      captureValues({
        projectInvoiceDate: invoiceDate ?? "",
        paymentTerms: paymentTerms ?? "",
        poNumber: poNumber ?? "",
        localInvoiceNumber: getValueOrBlank(localInvoiceNumber),
      });
    }
  }, [
    watch(ProjectRaiseInvoicePODetailsFormKey.projectPONumber),
    watch(ProjectRaiseInvoicePODetailsFormKey.projectInvoiceDate),
    watch(ProjectRaiseInvoicePODetailsFormKey.projectPaymentTerms),
    watch(ProjectRaiseInvoicePODetailsFormKey.localInvoiceNumber),
    purchaseOrdersList,
  ]);

  const isLoading =
    isGetClientByIdPending ||
    isPaymentTermsListPending ||
    isProjectPrimaryDetailsPending ||
    isGetKeyProjectPending;
  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isLoading,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoading]);

  const getPONumberList = useMemo(() => {
    if (isNotEmpty(purchaseOrdersList)) {
      return purchaseOrdersList?.map((item: POData) => ({
        label: item.name,
        value: item.id,
      }));
    }
    return [];
  }, [purchaseOrdersList]);

  const getRaiseInvoiceLabel = (
    key: keyof typeof projectRaiseInvoiceSiteCoreKeys,
  ) => {
    return fetchLabel(
      projectRaiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
      projectRaiseInvoiceSiteCoreKeys[key],
    );
  };

  const validatePOBalanceValue = () => {
    if (getValues(ProjectRaiseInvoicePODetailsFormKey.projectPONumber) === "")
      return false;
    return totalInvoiceAmount > currentPOBalance;
  };

  useEffect(() => {
    if (
      isGetClientByIdSuccess &&
      isProjectPrimaryDetailsSuccess &&
      isGetKeyProjectSuccess &&
      isNotEmpty(projectPrimaryDetails) &&
      isNotEmpty(clientData?.data) &&
      isNotEmpty(projectKeyDetailsResponse)
    ) {
      const fileName = `Proforma_Invoice_${new Date().toLocaleDateString()}.pdf`;
      const {
        officeAndContactDetails: { billingContact },
      } = projectPrimaryDetails;
      const {
        mailingAddress,
        profile: { clientName },
      } = clientData.data;
      const paymentTermsId = getValues(
        ProjectRaiseInvoicePODetailsFormKey.projectPaymentTerms,
      );
      const paymentTerms = paymentTermsList?.find(
        (term) => term.id === paymentTermsId,
      )?.name;
      const { header, footer1, footer2, footer3 } = projectKeyDetailsResponse;

      generatePDF(
        <ProjectProformaPdfTemplate
          projectName={projectName}
          billingEmail={billingContact?.email ?? ""}
          mailingAddress={mailingAddress}
          clientName={clientName}
          projectExpenseDetails={invoiceFeesAndExpense}
          totalInvoiceAmount={totalInvoiceAmount}
          billingCurrency={totalInvoiceCurrency}
          paymentTerms={paymentTerms}
          header={header}
          footer1={footer1}
          footer2={footer2}
          footer3={footer3}
        />,
        fileName,
      );
    }
  }, [
    isGetClientByIdSuccess,
    clientData,
    isProjectPrimaryDetailsSuccess,
    projectPrimaryDetails,
    isGetKeyProjectSuccess,
    projectKeyDetailsResponse,
    paymentTermsList,
  ]);

  const onClickDownloadProformaInvoice = async () => {
    getProjectPrimaryDetails(projectId);
    getClientById(clientId);
  };

  const downloadProformaInvoice = () => (
    <ModalContainer>
      <PermissionWrapper
        permission={MODULE_PERMISSIONS.PROJECT_VIEW_GENERATE_INVOICE}
      >
        <MuiBox className="download-performa-invoice">
          <DownloadIcon fontSize="small" className="download-icon" />
          <MuiTypography
            variant="h4Bold"
            onClick={onClickDownloadProformaInvoice}
          >
            {getRaiseInvoiceLabel("downloadProformaInvoiceLinkText")}
          </MuiTypography>
        </MuiBox>
      </PermissionWrapper>
    </ModalContainer>
  );

  const currencyList = useSelector(currencyListSelector);

  const getFormattedValueOrDash = (value: number | undefined): string => {
    return value
      ? getFormattedAmountWithCurrencyFromList(
          value,
          currencyList,
          totalInvoiceCurrency,
        )
      : EMPTY_VALUE_DASH;
  };

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      title={modalTitle}
      hasLargeContent
      disablePrimaryButton={!!Object.keys(formState.errors).length}
      primaryButtonTitle={
        validatePOBalanceValue()
          ? commonLabels.ok
          : getRaiseInvoiceLabel("invoiceButton")
      }
      onPrimaryButtonClick={
        validatePOBalanceValue()
          ? onClose
          : () => {
              handleSubmit(onPrimaryButtonClick)();
            }
      }
      secondaryButtonTitle={validatePOBalanceValue() ? "" : commonLabels.cancel}
      onSecondaryButtonClick={onClose}
      headerChildren={downloadProformaInvoice()}
    >
      <ModalContainer>
        <MuiBox className="fields-wrapper">
          <MuiTypography variant="h2Bold">
            {getRaiseInvoiceLabel("summarySectionHeader")}
          </MuiTypography>
          <MuiGrid container spacing={2}>
            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                label={getRaiseInvoiceLabel("summaryTotalAmount")}
                replaceEmptyWithDash
                value={getFormattedValueOrDash(totalBillingAmount)}
              />
            </MuiGrid>

            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                label={getRaiseInvoiceLabel("summaryTotalTaxAmount")}
                replaceEmptyWithDash
                value={getFormattedValueOrDash(totalTaxAmount)}
              />
            </MuiGrid>
            <MuiGrid size={gridSize}>
              <MuiDoubleLabel
                label={getRaiseInvoiceLabel("summaryTotalInvoiceAmount")}
                replaceEmptyWithDash
                value={`${getFormattedValueOrDash(totalInvoiceAmount)}`}
              />
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
        <MuiBox className="fee-and-expense-label">
          <MuiTypography variant="h3SemiBold">
            {getRaiseInvoiceLabel("feeAndExpensesSectionHeader")}
          </MuiTypography>
        </MuiBox>
        <ProjectRaiseInvoiceList
          projectInvoiceFeesAndExpense={invoiceFeesAndExpense}
          currencyName={totalInvoiceCurrency}
        />
        <MuiBox className="po-details-wrapper">
          <MuiBox className="po-details-header">
            <MuiTypography variant="h2Bold">
              {getRaiseInvoiceLabel("invoiceAndPODetailsSectionHeader")}
            </MuiTypography>
          </MuiBox>
          <MuiBox className="po-details-content">
            <MuiGrid container spacing={2}>
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={ProjectRaiseInvoicePODetailsFormKey.localInvoiceNumber}
                >
                  <MuiTextField
                    name={
                      ProjectRaiseInvoicePODetailsFormKey.localInvoiceNumber
                    }
                    id={`${pageId}-${ProjectRaiseInvoicePODetailsFormKey.localInvoiceNumber}`}
                    label={getRaiseInvoiceLabel("localInvoiceNumber")}
                    requiredARIALabel={commonARIALabel.required}
                    placeholder={
                      billingCountryId === SITE_CORE_COUNTRIES_VALUE.FRANCE
                        ? EMPTY_VALUE_DASH
                        : commonLabels.inputPlaceHolder
                    }
                    value=""
                    shouldTrimSpaces
                    maxLength={MAX_LENGTH_30}
                    showCloseIcon
                    disabled={
                      billingCountryId === SITE_CORE_COUNTRIES_VALUE.FRANCE
                    }
                  />
                </FormController>
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <MuiDateFieldFormController
                  control={control}
                  name={ProjectRaiseInvoicePODetailsFormKey.projectInvoiceDate}
                  format={userCountryDateFormat}
                  id={`${pageId}-${ProjectRaiseInvoicePODetailsFormKey.projectInvoiceDate}`}
                  value={
                    getValues(
                      ProjectRaiseInvoicePODetailsFormKey.projectInvoiceDate,
                    )
                      ? dayjs(
                          getValues(
                            ProjectRaiseInvoicePODetailsFormKey.projectInvoiceDate,
                          ),
                          userCountryDateFormat,
                        )
                      : dayjs()
                  }
                  label={getRaiseInvoiceLabel("invoiceDate")}
                  className="date-picker"
                  disablePast
                  isMandatory
                  requiredARIALabel={commonARIALabel.required}
                  selectDateARIALabel={commonARIALabel.selectDate}
                />
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <MuiTextField
                  name={
                    ProjectRaiseInvoicePODetailsFormKey.projectInvoiceLanguage
                  }
                  id={`${pageId}-${ProjectRaiseInvoicePODetailsFormKey.projectInvoiceLanguage}`}
                  value={getValueOrDash(projectInvoicingLanguages?.[0]?.name)}
                  label={getRaiseInvoiceLabel("invoiceLanguage")}
                  disabled={true}
                  requiredARIALabel={commonARIALabel.required}
                />
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={ProjectRaiseInvoicePODetailsFormKey.projectPaymentTerms}
                  key={ProjectRaiseInvoicePODetailsFormKey.projectPaymentTerms}
                >
                  <MuiSelect
                    label={getRaiseInvoiceLabel("paymentTermsDropDown")}
                    options={setApiDataToDropDownOption(paymentTermsList ?? [])}
                    value={""}
                    id={`${pageId}-paymentTerms`}
                    isMandatory
                    showSearch
                    onChange={(e: SelectChangeEvent<unknown>) => {
                      setValue(
                        ProjectRaiseInvoicePODetailsFormKey.projectPaymentTerms,
                        e.target.value as string,
                      );
                    }}
                    requiredARIALabel={commonARIALabel.required}
                    placeholder={commonLabels.selectPlaceHolder}
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>
            <MuiGrid container spacing={2}>
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={ProjectRaiseInvoicePODetailsFormKey.projectPONumber}
                  key={ProjectRaiseInvoicePODetailsFormKey.projectPONumber}
                >
                  <MuiSelect
                    label={getRaiseInvoiceLabel("poNumber")}
                    options={getPONumberList || []}
                    visibleDefaultSelectOption={true}
                    value={""}
                    id={`${pageId}-${ProjectRaiseInvoicePODetailsFormKey.projectPONumber}`}
                    placeholder={commonLabels.selectPlaceHolder}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>

              {getValues(
                ProjectRaiseInvoicePODetailsFormKey.projectPONumber,
              ) && (
                <>
                  <MuiGrid size={gridSize} className="client-code">
                    <FormController
                      control={control}
                      name={
                        ProjectRaiseInvoicePODetailsFormKey.projectCurrentPOBalance
                      }
                      key={
                        ProjectRaiseInvoicePODetailsFormKey.projectCurrentPOBalance
                      }
                    >
                      <MuiTextField
                        label={getRaiseInvoiceLabel("currentPOBalance")}
                        value={currentPOBalance}
                        id={`${pageId}-${ProjectRaiseInvoicePODetailsFormKey.projectCurrentPOBalance}`}
                        placeholder={
                          hasEmptyAmountPO()
                            ? EMPTY_VALUE_DASH
                            : `${totalInvoiceCurrency} ${currentPOBalance.toString()}`
                        }
                        disabled
                        requiredARIALabel={commonARIALabel.required}
                        icon={
                          hasEmptyAmountPO() && (
                            <WarningIcon className="po-shortage-icon" />
                          )
                        }
                      />
                    </FormController>
                  </MuiGrid>
                  {hasShortagePO() && (
                    <MuiGrid size={gridSize} className="client-code">
                      <FormController
                        control={control}
                        name={
                          ProjectRaiseInvoicePODetailsFormKey.projectPOShortage
                        }
                        key={
                          ProjectRaiseInvoicePODetailsFormKey.projectPOShortage
                        }
                      >
                        <MuiTextField
                          label={getRaiseInvoiceLabel("poShortage")}
                          value={String(
                            (totalInvoiceAmount - currentPOBalance).toFixed(2),
                          )}
                          id={`${pageId}-${ProjectRaiseInvoicePODetailsFormKey.projectPOShortage}`}
                          placeholder={String(
                            (totalInvoiceAmount - currentPOBalance).toFixed(2),
                          )}
                          disabled
                          icon={<WarningIcon className="po-shortage-icon" />}
                          requiredARIALabel={commonARIALabel.required}
                        />
                      </FormController>
                    </MuiGrid>
                  )}
                </>
              )}
            </MuiGrid>
          </MuiBox>
          {showPOBottomError && (
            <MuiBox className="additional-info">
              <InformationBadge
                icon={<WarningIcon className="warning-icon" />}
                position="start"
                message={poBottomErrorMessage}
              />
            </MuiBox>
          )}
        </MuiBox>
      </ModalContainer>
    </MuiModal>
  );
};
